import React, { FC, memo, useCallback, useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { RequestSettlementRequestDto } from '@sparling/financial-common'
import { Modal } from '../../../../../../components/Dashboard/Modal'
import { InputField } from '../../../../../../components/Dashboard/InputField'
import { InputSelect } from '../../../../../../components/Dashboard/InputSelect'
import { Loader } from '../../../../../../components/Loader'
import {
  balancesSelector,
  availableListForSettlementRequestSelector,
  isLoadingRequestSettlementSelector,
  isLoadingUserWalletsListSelector,
  userActiveBankAccountsSelector,
  userActiveWalletsSelector,
} from '../../../../../../store/selectors'
import {
  createSettlementRequest,
  getUserBankAccountListRequest,
  getUserWalletListRequest,
} from '../../../../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    marginTop: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  input: {
    '& label': {
      paddingBottom: theme.spacing(0.5),
    },
  },
  emptyWalletsText: {
    marginLeft: theme.spacing(3),
  },
}))

interface IRequestSettlementProps {
  isOpen: boolean
  modalClose: () => void
}

const RequestSettlementPopup: FC<IRequestSettlementProps> = ({ isOpen, modalClose }) => {
  const dispatch = useDispatch()
  const isLoadingUserWalletsList = useSelector(isLoadingUserWalletsListSelector)
  const settlement = useSelector(availableListForSettlementRequestSelector)
  const bankAccounts = useSelector(userActiveBankAccountsSelector)
  const wallets = useSelector(userActiveWalletsSelector)
  const { data: balances } = useSelector(balancesSelector)
  const isLoadingRequestSettlement = useSelector(isLoadingRequestSettlementSelector)
  const isBalanceZero = !balances.length || !balances.some((b) => Number(b.amount) > 0)
  const isBankAccountOrWalletExists = bankAccounts?.length || wallets?.length
  const isDisabledForm = !isBankAccountOrWalletExists || isBalanceZero

  const formMethods = useForm<RequestSettlementRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(RequestSettlementRequestDto),
    defaultValues: {
      ...(settlement.length && {
        currency: settlement[0]?.value,
        address: settlement[0]?.address,
      }),
      amount: 0,
      ...(balances.length && { balances }),
    },
    context: { balances },
  })

  const {
    handleSubmit,
    getValues,
    watch,
    setValue,
    register,
    formState: { isValid },
  } = formMethods

  const classes = useStyles()

  useEffect(() => {
    setValue('balances', balances || [])
  }, [balances, setValue])

  useEffect(() => {
    if (settlement.length && !getValues('currency')) {
      setValue('currency', settlement[0]?.value)
      if (settlement[0]?.address) setValue('address', settlement[0]?.address)
    }
  }, [getValues, setValue, settlement])

  useEffect(() => {
    dispatch(getUserWalletListRequest())
    dispatch(getUserBankAccountListRequest())
  }, [dispatch])

  const onSubmit = useCallback(
    (data) => {
      dispatch(createSettlementRequest(data))
    },
    [dispatch],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'currency' && value.currency) {
        const wallet = settlement.find((elem) => elem.value === value.currency)
        if (wallet?.address) setValue('address', wallet.address)
        else setValue('address', '')
      }
    })
    return () => subscription.unsubscribe()
  }, [settlement, setValue, watch])

  const address = useWatch({ name: 'address', control: formMethods.control })

  return (
    <Modal
      title="Request Settlement"
      isOpen={isOpen}
      modalClose={modalClose}
      isSubmit={!isDisabledForm}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid}
    >
      {(isLoadingUserWalletsList || isLoadingRequestSettlement) && <Loader />}
      <FormProvider {...formMethods}>
        <input type="hidden" {...register('balances')} />
        <Grid container spacing={3} className={classes.body}>
          {isDisabledForm ? (
            <Typography className={classes.emptyWalletsText}>
              {!isBankAccountOrWalletExists
                ? 'Please add wallet or bank account and go through the whitelisting process.'
                : 'Your balance is zero. You need funds in your account to proceed with a withdrawal.'}
            </Typography>
          ) : (
            <>
              <Grid item xs={6} className={classes.input}>
                <InputSelect
                  inputName="currency"
                  labelName={'Select Asset'}
                  width={'100%'}
                  enums={settlement}
                  defaultValue={settlement[0]?.value}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField inputName="amount" labelName={'Amount'} inputType="text" width={'100%'} />
              </Grid>

              {address && (
                <Grid item xs={12} className={classes.input}>
                  <InputField
                    labelName={'Address'}
                    inputName={'address'}
                    inputType={'text'}
                    width={'100%'}
                    marginBottom={'0px'}
                    defaultValue={address}
                    disabled
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoRequestSettlementPopup = memo(RequestSettlementPopup)
